import { uniq } from "lodash";
import { useEffect, useMemo, type FC } from "react";

import { useGetAggregatedEntityIds } from "@ds/hooks/use-get-aggregated-entity-ids";
import { useDeepRootSelector, useRootDispatch, useRootSelector } from "@ds/hooks";

import { EntityTypeName, isAggregatedEntity } from "@ds/model/entity-model";

import { getDeploymentStatusIndicatorWithNameAndLink } from "@ds/modules/deployments/components/common/status-indicator";
import { selectDeploymentsByVersions, selectDeploymentsIsLoading } from "@ds/modules/deployments/redux/selectors";
import { deploymentsActions } from "@ds/modules/deployments/redux/slice";

import { MultipleValuesLabelWithLink, UnassignedLabel } from "@ds/components/forms/labels/common";
import { InfoAttribute, InfoSection } from "@ds/components/info-view";
import { SpinnerLoader2 } from "@ds/components/loaders/loaders";
import { formatAbsoluteDate } from "@ds/utils/date";
import { composeDetailsRouteByEntityIds } from "@ds/utils/router";

import { selectPlayersByIds } from "../../redux/selectors";

export const PlayerDeployedContentInfoSection: FC<{ player: Player }> = ({ player }) => {
  const aggregatedPlayerIds = useGetAggregatedEntityIds(player);
  const aggregatedPlayers = useDeepRootSelector(state => selectPlayersByIds(state, aggregatedPlayerIds));

  const contentVersions = useMemo(
    () => uniq(aggregatedPlayers.map(({ content_version }) => content_version).filter((v): v is number => !!v)),
    [aggregatedPlayers],
  );

  const contentDeployments = useDeepRootSelector(state => selectDeploymentsByVersions(state, contentVersions));

  const isLoading = useRootSelector(
    state => selectDeploymentsIsLoading(state) || (!!contentVersions.length && !contentDeployments.length),
  );

  const dispatch = useRootDispatch();
  useEffect(() => {
    contentVersions.forEach(version => {
      dispatch(deploymentsActions.fetchDeployments({ version, type: "content" }));
    });
  }, [dispatch, contentVersions]);

  const infoBody = useMemo(() => {
    if (isLoading) {
      return <SpinnerLoader2 />;
    }

    if (isAggregatedEntity(player) && contentDeployments.length > 1) {
      return (
        <MultipleValuesLabelWithLink
          to={composeDetailsRouteByEntityIds(
            contentDeployments.map(({ id }) => id),
            EntityTypeName.DEPLOYMENT,
          )}
        />
      );
    } else if (!contentDeployments[0]) {
      return <UnassignedLabel text="No content has been deployed yet" />;
    }

    return (
      <>
        <InfoAttribute name="Deployment" value={getDeploymentStatusIndicatorWithNameAndLink(contentDeployments[0])} />
        <InfoAttribute name="Version" value={contentDeployments[0].version} />
        <InfoAttribute name="When" value={formatAbsoluteDate(contentDeployments[0].ended_at)} />
      </>
    );
  }, [isLoading, player, contentDeployments]);

  return (
    <InfoSection title="Deployed Content" icon="pi pi-box">
      {infoBody}
    </InfoSection>
  );
};
